import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Tag, Tooltip } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

import { RequirementConfirm, RequirementConfirmShowEnum, RequirementConfirmTypeEnum, RequirementConfirmValueEnum } from '@/types/requirement';
import { ReviewDesc, ReviewDescTip } from './style';

interface RequirementReviewProps {
  confirms: Array<RequirementConfirm>;
  isSHowLine?: boolean;
}

interface RequirementConfirmProps extends RequirementConfirm {
  descEle: React.ReactNode;
  statusEle: React.ReactNode;
  iconEle: React.ReactNode;
}

const RequirementReview: React.FC<RequirementReviewProps> = ({
  confirms,
  isSHowLine = false,
}) => {
  const { t } = useTranslation();

  const [newConfirms, setNewConfirms] = useState<Array<RequirementConfirmProps>>([])

  const statusTip = {
    [RequirementConfirmTypeEnum.MATERIAL_CONTROL]: {
      status: t("requirement.materialStatus"),
      desc: t("requirement.materialDesc")
    },
    [RequirementConfirmTypeEnum.PROD]: {
      status: t("requirement.prodStatus"),
      desc: t("requirement.prodDesc")
    },
    [RequirementConfirmTypeEnum.PURCHASE]: {
      status: t("requirement.purchaseStatus"),
      desc: t("requirement.purchaseDesc")
    },
    [RequirementConfirmTypeEnum.FINANCE]: {
      status: t("requirement.financeStatus"),
      desc: t("requirement.financeDesc")
    },
    [RequirementConfirmTypeEnum.SALE]: {
      status: t("requirement.saleStatus"),
      desc: t("requirement.saleDesc")
    },
    [RequirementConfirmTypeEnum.COMMODITY]: {
      status: t("requirement.commodityStatus"),
      desc: t("requirement.commodityDesc")
    }
  }

  useEffect(() => {
    const showProgress = confirms?.filter(confirm => confirm?.show !== RequirementConfirmShowEnum.HIDDEN);
    const sortProgress = showProgress?.sort((a, b) => a.sort - b.sort) ?? []

    const progress = sortProgress.map((progress, index) => ({
      ...progress,
      descEle: (
        <div key={`${progress.type}-${index}`}>
          {/* <ReviewDescTip>{index + 1}. {statusTip[progress.type]?.desc}:</ReviewDescTip> <ReviewDesc>{isPassStatus(progress, index, sortProgress) ? (progress?.desc || '') : ''}</ReviewDesc> */}
          <ReviewDescTip>{index + 1}. {statusTip[progress.type]?.desc}:</ReviewDescTip> <ReviewDesc style={{ color: isPassStatus(progress, index, sortProgress) ? "grey" : "#FF4D4F" }}>{progress?.desc || ''}</ReviewDesc>
        </div>
      ),
      statusEle: (
        <div key={`status-${progress.type}-${index}`}>
          {statusTip[progress.type]?.status}:
          <Fragment>
            {isPassStatus(progress, index, sortProgress) && <Tag color='green'>{t('requirement.oked')}</Tag>}
            {isPendingStatus(progress, index, sortProgress) && <Tag color='blue'>{t('requirement.pending')}</Tag>}
            {isRejectStatus(progress, index, sortProgress) && <Tag color='red'>{t('requirement.rejected')}</Tag>}
          </Fragment>
        </div>
      ),
      iconEle: (
        <div key={`icon-${progress.type}-${index}`}>
          {isPendingStatus(progress, index, sortProgress) && <CheckCircleOutlined style={{ color: 'grey', fontSize: 18 }} />}
          {isPassStatus(progress, index, sortProgress) && <CheckCircleOutlined style={{ color: 'green', fontSize: 18 }} />}
          {isRejectStatus(progress, index, sortProgress) && <CloseCircleOutlined style={{ color: 'red', fontSize: 18 }} />}
        </div>
      )
    }))

    setNewConfirms(() => progress)
  }, [confirms])

  const isPassStatus = (item: RequirementConfirm, index: number, cons: Array<RequirementConfirm>) => {
    return item.value === RequirementConfirmValueEnum.PASSED || item.value === RequirementConfirmValueEnum.REJECTED
  }

  const isPendingStatus = (item: RequirementConfirm, index: number, cons: Array<RequirementConfirm>) => {
    return [RequirementConfirmValueEnum.NOT_PASS, null].includes(item.value) && cons[index - 1]?.value !== RequirementConfirmValueEnum.REJECTED
  }

  const isRejectStatus = (item: RequirementConfirm, index: number, cons: Array<RequirementConfirm>) => {
    return item.value === null && cons[index - 1]?.value === RequirementConfirmValueEnum.REJECTED
  }

  return (
    isSHowLine ? <Flex>
      {newConfirms.map((item, i) => (
        <div key={`tooltip-${i}-${item.type}`}>{item.statusEle}</div>
      ))}
    </Flex>
      : <Flex vertical>
        <div style={{ width: 200 }}>
          <Tooltip placement='right' title={
            <Flex vertical gap={10}>
              {newConfirms.map((item, i) => (
                <div key={`tooltip-${i}-${item.type}`}>{item.statusEle}</div>
              ))}
            </Flex>
          }>
            <Flex gap={6}>
              {newConfirms.map((item, i) => (
                <div key={`icon-${i}-${item.type}`}>{item.iconEle}</div>
              ))}
            </Flex>
          </Tooltip>
        </div>
      </Flex>
  )
};

export default RequirementReview;
